import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { billApi } from '../BillCustomer/billApi';
import { customerApi } from '../Onboarding/CustomerOnboarding/customerApi';
import { getTenantInfo } from '../OrgManagement/employeeApiService';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card } from '../ui';
import Input from '../ui/input';
import PrimaryButton from '../ui/PrimaryButton';

const createEmptyItemRow = () => ({
  item: '',
  quantity: '',
  pricePerUnit: '',
});

const resolveSiteValue = (site) => {
  return site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? site?.site_code ?? site?.siteCode ?? '';
};

const resolveSiteLabel = (site) => {
  return site?.site_name || site?.name || site?.siteCode || site?.site_code || `Site ${site?.site_id ?? site?.id ?? ''}`;
};

// Helper function to capitalize first letter
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Calculate number of days between two dates
const calculateDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 1;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 1;
};

const FacilityBookingBillCustomer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get booking data from navigation state
  const bookingData = location.state?.bookingData || null;
  const invoiceDataFromNav = location.state?.invoiceData || null; // Invoice data if coming from invoice
  const [fullBookingData, setFullBookingData] = useState(bookingData);
  
  // Booking details state for editable fields - must be declared before useEffects that use it
  const [bookingDetails, setBookingDetails] = useState({
    purposeOfBooking: bookingData?.purposeOfBooking || '',
    startDate: bookingData?.startDate || '',
    endDate: bookingData?.endDate || '',
    startTime: bookingData?.startTime || '',
    endTime: bookingData?.endTime || '',
    whosBooking: bookingData?.whosBooking || '',
    blockNumber: bookingData?.blockNumber || '',
    apartmentFlatNo: bookingData?.apartmentFlatNo || '',
    numberOfPeople: bookingData?.numberOfPeople || '',
    notes: bookingData?.notes || '',
  });
  
  // Fetch full booking details if we have a bookingId but missing data
  useEffect(() => {
    const fetchFullBookingDetails = async () => {
      // Always fetch if we have a bookingId to ensure we have complete data
      // This is especially important when coming from calendar events which may have incomplete data
      const needsFetch = bookingData?.bookingId;
      
      if (needsFetch) {
        try {
          const { facilityBookingApi } = await import('./facilityBookingApi');
          const fullBooking = await facilityBookingApi.getBookingById(bookingData.bookingId);
          
          if (fullBooking) {
            // Handle API response structure - could be data.booking or just the booking object
            const booking = fullBooking.data?.booking || fullBooking.data || fullBooking.booking || fullBooking;
            
            // Helper to check if a value is meaningful (not empty/null/undefined)
            const hasValue = (val) => val !== undefined && val !== null && val !== '' && String(val).trim() !== '';
            
            // Helper to get value from API or navigation, ALWAYS preferring API when it has a meaningful value
            const getValue = (apiVal, navVal) => {
              // ALWAYS prefer API value if it exists and is meaningful
              if (hasValue(apiVal)) {
                return apiVal;
              }
              // Otherwise use navigation value if it exists
              if (hasValue(navVal)) {
                return navVal;
              }
              // Otherwise return empty string
              return '';
            };
            
            // Extract API values (handle both snake_case and camelCase)
            const apiWhosBooking = booking.whos_booking || booking.whosBooking;
            const apiEmail = booking.email || booking.email; // email is same in both cases, but checking both for consistency
            const apiNumberOfPeople = booking.number_of_people !== null && booking.number_of_people !== undefined 
              ? String(booking.number_of_people) 
              : (booking.numberOfPeople !== null && booking.numberOfPeople !== undefined 
                  ? String(booking.numberOfPeople) 
                  : '');
            const apiNotes = booking.notes || booking.booking_notes;
            
            // Merge API data (snake_case) with existing data
            // ALWAYS prefer API values when they exist, fallback to navigation data
            const mergedData = {
              ...bookingData,
              // whos_booking - ALWAYS prefer API if exists, otherwise navigation, otherwise empty
              whosBooking: getValue(apiWhosBooking, bookingData.whosBooking),
              purposeOfBooking: getValue(booking.purpose_of_booking || booking.purposeOfBooking, bookingData.purposeOfBooking),
              // Handle number_of_people - ALWAYS prefer API, convert to string, handle null
              numberOfPeople: getValue(apiNumberOfPeople, 
                bookingData.numberOfPeople !== null && bookingData.numberOfPeople !== undefined 
                  ? String(bookingData.numberOfPeople) 
                  : ''
              ),
              // notes - ALWAYS prefer API, then navigation
              notes: getValue(apiNotes, bookingData.notes),
              blockNumber: bookingData.blockNumber || booking.block_number || booking.blockNumber || '',
              apartmentFlatNo: bookingData.apartmentFlatNo || booking.apartment_flat_no || booking.apartmentFlatNo || '',
              contactNumber: bookingData.contactNumber || booking.contact_number || booking.contactNumber || '',
              email: getValue(apiEmail, bookingData.email),
              startDate: bookingData.startDate || booking.start_date || booking.startDate || '',
              endDate: bookingData.endDate || booking.end_date || booking.endDate || '',
              startTime: bookingData.startTime || (booking.start_time ? booking.start_time.split('T')[1]?.slice(0, 5) : null) || booking.startTime || '',
              endTime: bookingData.endTime || (booking.end_time ? booking.end_time.split('T')[1]?.slice(0, 5) : null) || booking.endTime || '',
            };
            setFullBookingData(mergedData);
            // Update booking details state with merged data - ensure we use API values when available
            setBookingDetails(prev => {
              // Only update if we have new values from API, otherwise keep existing user input
              const updated = {
                purposeOfBooking: mergedData.purposeOfBooking || prev.purposeOfBooking || '',
                startDate: mergedData.startDate || prev.startDate || '',
                endDate: mergedData.endDate || prev.endDate || '',
                startTime: mergedData.startTime || prev.startTime || '',
                endTime: mergedData.endTime || prev.endTime || '',
                whosBooking: mergedData.whosBooking || prev.whosBooking || '',
                blockNumber: mergedData.blockNumber || prev.blockNumber || '',
                apartmentFlatNo: mergedData.apartmentFlatNo || prev.apartmentFlatNo || '',
                numberOfPeople: hasValue(mergedData.numberOfPeople) 
                  ? String(mergedData.numberOfPeople) 
                  : (prev.numberOfPeople || ''),
                notes: hasValue(mergedData.notes) 
                  ? mergedData.notes 
                  : (prev.notes || ''),
              };
              return updated;
            });
          }
        } catch (error) {
          // Keep existing bookingData if fetch fails
          setFullBookingData(bookingData);
          setBookingDetails({
            purposeOfBooking: bookingData?.purposeOfBooking || '',
            startDate: bookingData?.startDate || '',
            endDate: bookingData?.endDate || '',
            startTime: bookingData?.startTime || '',
            endTime: bookingData?.endTime || '',
            whosBooking: bookingData?.whosBooking || '',
            blockNumber: bookingData?.blockNumber || '',
            apartmentFlatNo: bookingData?.apartmentFlatNo || '',
            numberOfPeople: bookingData?.numberOfPeople !== undefined && bookingData?.numberOfPeople !== null 
              ? String(bookingData.numberOfPeople) 
              : '',
            notes: bookingData?.notes || '',
          });
        }
      } else {
        setFullBookingData(bookingData);
        setBookingDetails({
          purposeOfBooking: bookingData?.purposeOfBooking || '',
          startDate: bookingData?.startDate || '',
          endDate: bookingData?.endDate || '',
          startTime: bookingData?.startTime || '',
          endTime: bookingData?.endTime || '',
          whosBooking: bookingData?.whosBooking || '',
          blockNumber: bookingData?.blockNumber || '',
          apartmentFlatNo: bookingData?.apartmentFlatNo || '',
          numberOfPeople: bookingData?.numberOfPeople !== undefined && bookingData?.numberOfPeople !== null 
            ? String(bookingData.numberOfPeople) 
            : '',
          notes: bookingData?.notes || '',
        });
      }
    };
    
    fetchFullBookingDetails();
  }, [bookingData]);

  // Populate rate and tax rates from invoice data when available
  // This runs after items and taxRates are initialized
  useEffect(() => {
    const populateFromInvoiceData = () => {
      // Check if we have invoice data from navigation
      if (invoiceDataFromNav) {
        console.log('📋 Found invoice data from navigation:', invoiceDataFromNav);
        
        // Populate rate from invoice items
        if (invoiceDataFromNav.items && invoiceDataFromNav.items.length > 0) {
          const firstItem = invoiceDataFromNav.items[0];
          if (firstItem.rate) {
            console.log('💰 Populating rate from invoice:', firstItem.rate);
            setItems(prev => {
              if (prev.length > 0 && prev[0]?.item.includes('Booking')) {
                return [{
                  ...prev[0],
                  pricePerUnit: String(firstItem.rate),
                }];
              }
              return prev;
            });
          }
        }
        
        // Populate tax rates from invoice data
        if (invoiceDataFromNav.cgstRate || invoiceDataFromNav.sgstRate) {
          console.log('📊 Populating tax rates from invoice:', {
            cgst: invoiceDataFromNav.cgstRate,
            sgst: invoiceDataFromNav.sgstRate,
          });
          setTaxRates({
            cgst: invoiceDataFromNav.cgstRate ? String(invoiceDataFromNav.cgstRate) : '',
            sgst: invoiceDataFromNav.sgstRate ? String(invoiceDataFromNav.sgstRate) : '',
          });
        }
        return;
      }
      
      // Check stored bill data by booking ID or booking signature (from invoice)
      const bookingId = fullBookingData?.bookingId || bookingData?.bookingId;
      const currentBooking = fullBookingData || bookingData;
      
      // Create a function to get booking key (same logic as invoice)
      const getBookingKey = () => {
        if (bookingId) {
          return `facilityBookingBillData_${bookingId}`;
        }
        // Use booking signature: siteId_facilityType_startDate_endDate_residentName
        if (currentBooking) {
          const signature = [
            currentBooking.siteId || '',
            currentBooking.facilityType || '',
            currentBooking.startDate || '',
            currentBooking.endDate || '',
            currentBooking.residentName || '',
          ].join('_');
          return `facilityBookingBillData_${signature}`;
        }
        return null;
      };
      
      const billDataKey = getBookingKey();
      console.log('🔑 Generated booking key for retrieval:', billDataKey);
      
      // Also generate signature key in case invoice stored with signature (when bookingId was undefined)
      const getSignatureKey = () => {
        if (currentBooking) {
          const signature = [
            currentBooking.siteId || '',
            currentBooking.facilityType || '',
            currentBooking.startDate || '',
            currentBooking.endDate || '',
            currentBooking.residentName || '',
          ].join('_');
          return `facilityBookingBillData_${signature}`;
        }
        return null;
      };
      
      const signatureKey = getSignatureKey();
      console.log('🔑 Generated signature key for retrieval:', signatureKey);
      
      if (typeof window !== 'undefined') {
        try {
          // Try bookingId key first
          let storedBillData = null;
          let foundKey = null;
          
          if (billDataKey) {
            storedBillData = localStorage.getItem(billDataKey);
            console.log('🔍 Checking localStorage for bookingId key:', billDataKey, 'Found:', !!storedBillData);
            if (storedBillData) {
              foundKey = billDataKey;
            }
          }
          
          // If not found, try signature key
          if (!storedBillData && signatureKey) {
            storedBillData = localStorage.getItem(signatureKey);
            console.log('🔍 Checking localStorage for signature key:', signatureKey, 'Found:', !!storedBillData);
            if (storedBillData) {
              foundKey = signatureKey;
            }
          }
          
          // If still not found, search all localStorage keys for matching bill data
          if (!storedBillData && currentBooking) {
            console.log('🔍 Searching all localStorage keys for matching bill data...');
            try {
              const siteId = currentBooking.siteId || '';
              const residentName = currentBooking.residentName || '';
              
              let bestMatch = null;
              let bestMatchKey = null;
              let bestMatchTimestamp = 0;
              
              // Search through all localStorage keys
              for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('facilityBookingBillData_')) {
                  try {
                    const data = JSON.parse(localStorage.getItem(key));
                    // Match by siteId and residentName (most reliable identifiers)
                    if (data && 
                        (siteId && key.includes(siteId)) && 
                        (residentName && key.includes(residentName))) {
                      // Prefer the most recent data
                      const timestamp = data.timestamp || 0;
                      if (timestamp > bestMatchTimestamp) {
                        bestMatch = data;
                        bestMatchKey = key;
                        bestMatchTimestamp = timestamp;
                      }
                    }
                  } catch (e) {
                    // Skip invalid JSON
                  }
                }
              }
              
              if (bestMatch) {
                storedBillData = JSON.stringify(bestMatch);
                foundKey = bestMatchKey;
                console.log('✅ Found matching bill data by siteId and residentName:', foundKey, bestMatch);
              }
            } catch (error) {
              console.error('Error searching localStorage:', error);
            }
          }
          
          if (storedBillData) {
            const billData = JSON.parse(storedBillData);
            console.log('📋 Found stored bill data with key:', foundKey, billData);
            
            // Populate rate
            if (billData.rate) {
              console.log('💰 Populating rate from stored bill data:', billData.rate);
              setItems(prev => {
                if (prev.length > 0 && prev[0]?.item.includes('Booking')) {
                  // Always update if we have stored rate data
                  return [{
                    ...prev[0],
                    pricePerUnit: String(billData.rate),
                  }];
                }
                return prev;
              });
            }
            
            // Populate tax rates (even if one is empty)
            console.log('📊 Populating tax rates from stored bill data:', {
              cgst: billData.cgstRate,
              sgst: billData.sgstRate,
            });
            setTaxRates({
              cgst: billData.cgstRate ? String(billData.cgstRate) : '',
              sgst: billData.sgstRate ? String(billData.sgstRate) : '',
            });
            return; // Don't check cache if we found stored data
          }
          
          // Fallback: Check the invoice cache (for unsaved invoice data)
          const cacheKey = 'facilityBookingInvoice_cache';
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const cachedData = JSON.parse(cached);
            console.log('📋 Found cached invoice data (fallback):', cachedData);
            
            // Check if cached invoice data has rate and tax rates
            if (cachedData.lineItems && cachedData.lineItems.length > 0) {
              const firstLineItem = cachedData.lineItems[0];
              if (firstLineItem.rate) {
                console.log('💰 Populating rate from cache:', firstLineItem.rate);
                setItems(prev => {
                  if (prev.length > 0 && prev[0]?.item.includes('Booking') && !prev[0].pricePerUnit) {
                    return [{
                      ...prev[0],
                      pricePerUnit: String(firstLineItem.rate),
                    }];
                  }
                  return prev;
                });
              }
            }
            
            // Check taxRows for CGST and SGST
            if (cachedData.taxRows && Array.isArray(cachedData.taxRows)) {
              const cgstRow = cachedData.taxRows.find(row => row.label === 'CGST');
              const sgstRow = cachedData.taxRows.find(row => row.label === 'SGST');
              
              if (cgstRow || sgstRow) {
                console.log('📊 Populating tax rates from cache:', {
                  cgst: cgstRow?.percentage,
                  sgst: sgstRow?.percentage,
                });
                setTaxRates(prev => ({
                  cgst: cgstRow?.percentage ? String(cgstRow.percentage) : prev.cgst,
                  sgst: sgstRow?.percentage ? String(sgstRow.percentage) : prev.sgst,
                }));
              }
            }
          }
        } catch (error) {
          console.error('Error reading invoice cache:', error);
        }
      }
    };
    
    // Run after a small delay to ensure items state is initialized
    const timeoutId = setTimeout(populateFromInvoiceData, 100);
    return () => clearTimeout(timeoutId);
  }, [
    invoiceDataFromNav, 
    fullBookingData?.bookingId, 
    bookingData?.bookingId,
    fullBookingData?.siteId,
    fullBookingData?.facilityType,
    fullBookingData?.startDate,
    fullBookingData?.endDate,
    fullBookingData?.residentName,
    bookingData?.siteId,
    bookingData?.facilityType,
    bookingData?.startDate,
    bookingData?.endDate,
    bookingData?.residentName,
  ]);
  
  // Calculate days from booking
  const bookingDays = fullBookingData ? calculateDays(fullBookingData.startDate, fullBookingData.endDate) : 1;

  const [items, setItems] = useState(
    fullBookingData
      ? [
          {
            item: `${fullBookingData.facilityType || 'Facility'} Booking`,
            quantity: String(bookingDays),
            pricePerUnit: '',
          },
        ]
      : [createEmptyItemRow()]
  );
  const [selectedSite, setSelectedSite] = useState('');
  const [customerSelection, setCustomerSelection] = useState('new');
  const [customerDetails, setCustomerDetails] = useState({
    name: fullBookingData?.residentName || '',
    mobile: fullBookingData?.contactNumber || '',
    email: fullBookingData?.email || '',
  });
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [taxRates, setTaxRates] = useState({
    cgst: '',
    sgst: '',
  });
  const [isMobileView, setIsMobileView] = useState(false);
  
  // API integration state
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [isCustomersLoading, setIsCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitFeedback, setSubmitFeedback] = useState({ success: '', error: '' });
  const [tenantInfo, setTenantInfo] = useState(null);
  const [isLoadingTenant, setIsLoadingTenant] = useState(false);
  
  // Get overlay functions from context
  const { openOverlay } = useOverlay();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (event) => setIsMobileView(event.matches);
    handleChange(mediaQuery);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Fetch sites
  useEffect(() => {
    let isMounted = true;
    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError('');
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        if (isMounted) {
          setSites(sorted);
          
          // Auto-select the first site if coming from facility booking
          if (bookingData && sorted.length > 0 && !selectedSite) {
            const firstSiteValue = resolveSiteValue(sorted[0]);
            if (firstSiteValue) {
              setSelectedSite(firstSiteValue);
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          setSitesError(error?.message || 'Unable to load sites. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsSitesLoading(false);
        }
      }
    };
    fetchSites();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch customers
  useEffect(() => {
    let isMounted = true;
    const fetchCustomers = async () => {
      setIsCustomersLoading(true);
      setCustomersError('');
      try {
        const result = await customerApi.listCustomers({ limit: 100, status: 'active' });
        if (result.success) {
          const sorted = [...(result.customers || [])].sort((a, b) =>
            (a?.customer_name || a?.name || '').localeCompare(b?.customer_name || b?.name || '')
          );
          if (isMounted) {
            setCustomers(sorted);
          }
        } else {
          if (isMounted) {
            setCustomersError(result.error || 'Failed to load customers');
          }
        }
      } catch (error) {
        if (isMounted) {
          setCustomersError(error?.message || 'Unable to load customers. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsCustomersLoading(false);
        }
      }
    };
    fetchCustomers();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch tenant info
  useEffect(() => {
    let isMounted = true;
    const fetchTenantInfo = async () => {
      setIsLoadingTenant(true);
      try {
        const tenantData = await getTenantInfo();
        if (isMounted) {
          setTenantInfo(tenantData);
        }
      } catch (error) {
        console.error('Error fetching tenant info:', error);
        if (isMounted) {
          setTenantInfo(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingTenant(false);
        }
      }
    };
    fetchTenantInfo();
    return () => {
      isMounted = false;
    };
  }, []);

  // Update customer details when fullBookingData is loaded/updated
  useEffect(() => {
    if (fullBookingData && customerSelection === 'new') {
      setCustomerDetails(prev => ({
        name: fullBookingData.residentName || prev.name || '',
        mobile: fullBookingData.contactNumber || prev.mobile || '',
        email: fullBookingData.email || prev.email || '',
      }));
    }
  }, [fullBookingData, customerSelection]);

  const computeRowSubtotal = (row) => {
    const qty = parseFloat(row.quantity) || 0;
    const price = parseFloat(row.pricePerUnit) || 0;
    return qty * price;
  };

  const subtotal = useMemo(
    () => items.reduce((sum, row) => sum + computeRowSubtotal(row), 0),
    [items]
  );

  const cgstAmount = useMemo(() => {
    const cgstRate = parseFloat(taxRates.cgst) || 0;
    return (subtotal * cgstRate) / 100;
  }, [subtotal, taxRates.cgst]);

  const sgstAmount = useMemo(() => {
    const sgstRate = parseFloat(taxRates.sgst) || 0;
    return (subtotal * sgstRate) / 100;
  }, [subtotal, taxRates.sgst]);

  const grandTotal = subtotal + cgstAmount + sgstAmount;

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddItem = () => {
    setItems((prev) => [...prev, createEmptyItemRow()]);
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      setItems([createEmptyItemRow()]);
      return;
    }
    setItems((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const handleCustomerChange = (field, value) => {
    setCustomerDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleTaxChange = (field, value) => {
    if (Number(value) < 0) {
      return;
    }
    setTaxRates((prev) => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (amount) =>
    Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!selectedSite) {
      setSubmitFeedback({ success: '', error: 'Please select a site' });
      return;
    }

    if (items.length === 0 || items.some(item => !item.item || !item.quantity || !item.pricePerUnit)) {
      setSubmitFeedback({ success: '', error: 'Please add at least one valid item with name, quantity, and price' });
      return;
    }

    if (customerSelection === 'existing' && !selectedCustomerId) {
      setSubmitFeedback({ success: '', error: 'Please select a customer' });
      return;
    }

    if (customerSelection === 'new' && !customerDetails.name.trim()) {
      setSubmitFeedback({ success: '', error: 'Please enter customer name' });
      return;
    }

    setIsSubmitting(true);
    setSubmitFeedback({ success: '', error: '' });

    try {
      // Generate order ID
      const orderId = `FB-ORD-${Date.now().toString().slice(-6)}`;
      
      // Get site details
      const site = sites.find(s => resolveSiteValue(s) === selectedSite);
      const siteName = resolveSiteLabel(site);
      const siteId = selectedSite;
      const siteAddress = site?.address || site?.site_address || '';
      
      // Get organization name from tenant info
      const organizationName = tenantInfo?.data?.organization_name || tenantInfo?.name || tenantInfo?.organization_name || 'Innoworx';

      // Map items to API format
      const mappedItems = items
        .filter(item => item.item && item.quantity && item.pricePerUnit)
        .map((row) => {
          const quantity = parseFloat(row.quantity) || 0;
          const pricePerUnit = parseFloat(row.pricePerUnit) || 0;
          const subtotal = quantity * pricePerUnit;
          
          return {
            name: row.item.trim(),
            quantity: quantity,
            price_per_unit: pricePerUnit,
            subtotal: subtotal,
            unit: 'days',
          };
        });

      // Build API payload
      const apiPayload = {
        order_id: orderId,
        site_id: siteId,
        items: mappedItems,
        subtotal: parseFloat(subtotal.toFixed(2)),
        total: parseFloat(grandTotal.toFixed(2)),
        bill_type: 'facility_booking',
      };

      // Add optional fields only if they have values
      if (siteName) {
        apiPayload.site_name = siteName;
      }
      if (cgstAmount > 0) {
        apiPayload.cgst = parseFloat(cgstAmount.toFixed(2));
      }
      if (sgstAmount > 0) {
        apiPayload.sgst = parseFloat(sgstAmount.toFixed(2));
      }

      // Add booking reference if available
      if (fullBookingData?.bookingId) {
        apiPayload.booking_id = fullBookingData.bookingId;
      }

      // Add customer
      if (customerSelection === 'existing' && selectedCustomerId) {
        apiPayload.customer_id = selectedCustomerId;
      } else if (customerSelection === 'new' || (!customerSelection && customerDetails.name.trim())) {
        apiPayload.customer = {
          name: customerDetails.name.trim(),
          mobile: customerDetails.mobile.trim() || undefined,
          email: customerDetails.email.trim() || undefined,
        };
      }

      // Create bill via API
      const result = await billApi.createBill(apiPayload);

      if (result.success) {
        setSubmitFeedback({ 
          success: `Bill created successfully! Bill Number: ${result.bill?.bill_number || 'N/A'}`,
          error: '' 
        });

        // Open Bill Preview overlay (same as Bill Customer) instead of navigating to invoice
        const billPayload = {
          orderNumber: orderId,
          billNumber: result.bill?.bill_number || `BILL-${Math.floor(100000 + Math.random() * 900000)}`,
          tableNumber: '',
          items: items.map((row) => ({
            ...row,
            subtotal: computeRowSubtotal(row),
          })),
          subtotal,
          discountCode: '',
          discountValue: 0,
          discountedSubtotal: subtotal,
          cgstRate: taxRates.cgst,
          sgstRate: taxRates.sgst,
          cgstAmount,
          sgstAmount,
          grandTotal,
          customerDetails: customerSelection === 'existing' && selectedCustomerId
            ? (result.bill?.customer || customerDetails)
            : customerDetails,
          generatedAt: new Date().toISOString(),
          apiBill: result.bill,
          siteDetails: {
            organizationName: organizationName,
            siteName: siteName,
            address: siteAddress,
          },
        };

        openOverlay('billingPrint', {
          bill: billPayload,
          formatCurrency: formatCurrency,
        });
      } else {
        throw new Error(result.error || 'Failed to create bill');
      }
    } catch (error) {
      console.error('Error creating bill:', error);
      setSubmitFeedback({ 
        success: '', 
        error: error.message || 'Failed to create bill. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBillReady =
    selectedSite.trim().length > 0 &&
    (customerSelection === 'existing' ? selectedCustomerId.trim().length > 0 : customerDetails.name.trim().length > 0) &&
    items.some((row) => computeRowSubtotal(row) > 0);

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="mx-auto flex w-full max-w-7xl flex-col gap-6"
      >
        {/* Header */}
        <div className="mb-2">
          <p className="text-sm uppercase tracking-wide text-gray-500">
            Billing · Amenity Booking
          </p>
          <h1 className="text-2xl font-semibold text-gray-900">Amenity Booking Bill</h1>
          <p className="text-sm text-gray-500">
            Create a bill for amenity booking charges.
          </p>
        </div>

        <Card.Glassmorphism className="p-4">
          <label className="mb-1 block text-sm font-semibold text-gray-600">
            Site <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
            disabled={isSitesLoading}
            required
          >
            <option value="" disabled hidden>Select a site</option>
            {sites.map((site) => (
              <option key={resolveSiteValue(site)} value={resolveSiteValue(site)}>
                {resolveSiteLabel(site)}
              </option>
            ))}
          </select>
          {sitesError && (
            <p className="mt-1 text-xs text-red-600">{sitesError}</p>
          )}
          {!isSitesLoading && !sites.length && !sitesError && (
            <p className="mt-1 text-xs text-gray-500">
              No sites available. Please create a site first.
            </p>
          )}
        </Card.Glassmorphism>

        {/* Booking Details Section */}
        <Card.Light className="p-3">
          <div className="mb-4 flex flex-col gap-2">
            <h2 className="text-2xl font-semibold text-gray-900">
              Booking Details
            </h2>
            <p className="text-sm text-gray-500">
              View amenity booking information and details.
            </p>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Purpose of Booking</label>
              <Input
                value={bookingDetails.purposeOfBooking}
                onChange={(e) => setBookingDetails({ ...bookingDetails, purposeOfBooking: e.target.value })}
                placeholder="Enter purpose"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Start Date</label>
              <Input
                type="date"
                value={bookingDetails.startDate}
                onChange={(e) => setBookingDetails({ ...bookingDetails, startDate: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">End Date</label>
              <Input
                type="date"
                value={bookingDetails.endDate}
                onChange={(e) => setBookingDetails({ ...bookingDetails, endDate: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Start Time</label>
              <Input
                type="time"
                value={bookingDetails.startTime}
                onChange={(e) => setBookingDetails({ ...bookingDetails, startTime: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">End Time</label>
              <Input
                type="time"
                value={bookingDetails.endTime}
                onChange={(e) => setBookingDetails({ ...bookingDetails, endTime: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Role</label>
              <Input
                value={capitalizeFirst(
                  fullBookingData?.whosBooking || 
                  bookingData?.whosBooking ||
                  ''
                )}
                readOnly
                placeholder="Role from booking"
                className="bg-gray-50 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Block Number</label>
              <Input
                value={bookingDetails.blockNumber}
                onChange={(e) => setBookingDetails({ ...bookingDetails, blockNumber: e.target.value })}
                placeholder="Enter block number"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Apartment/Flat No</label>
              <Input
                value={bookingDetails.apartmentFlatNo}
                onChange={(e) => setBookingDetails({ ...bookingDetails, apartmentFlatNo: e.target.value })}
                placeholder="Enter apartment/flat number"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Number of People</label>
              <Input
                type="number"
                min="0"
                value={bookingDetails.numberOfPeople || ''}
                onChange={(e) => setBookingDetails({ ...bookingDetails, numberOfPeople: e.target.value })}
                placeholder="Enter number of people"
              />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="mb-1 block text-xs font-semibold text-gray-500">Notes</label>
              <textarea
                value={bookingDetails.notes || ''}
                onChange={(e) => setBookingDetails({ ...bookingDetails, notes: e.target.value })}
                placeholder="Enter notes"
                rows={3}
                className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
              />
            </div>
          </div>
        </Card.Light>

        <Card.Light className="p-3">
          <div className="mb-4 flex flex-col gap-2">
            <h2 className="text-2xl font-semibold text-gray-900">
              Booking Charges
            </h2>
            <p className="text-sm text-gray-500">
              Add amenity booking charges with days and rate per day.
            </p>
          </div>

          <div className="relative">
            {!isMobileView && (
              <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-semibold">Item</th>
                      <th className="px-4 py-3 font-semibold">Days</th>
                      <th className="px-4 py-3 font-semibold">Rate per Day</th>
                      <th className="px-4 py-3 font-semibold text-right">
                        Subtotal
                      </th>
                      <th className="px-4 py-3 text-right font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row, index) => {
                      const rowSubtotal = computeRowSubtotal(row);
                      return (
                        <tr
                          key={`item-row-${index}`}
                          className="border-t border-gray-100"
                        >
                          <td className="px-4 py-3 align-top">
                            <Input
                              placeholder="Enter item"
                              value={row.item}
                              onChange={(event) =>
                                handleItemChange(index, 'item', event.target.value)
                              }
                              className="rounded-full border border-green-200 bg-white px-4 py-2 text-gray-800 placeholder:text-gray-400"
                            />
                          </td>
                          <td className="px-4 py-3 align-top">
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              placeholder="0"
                              value={row.quantity}
                              onChange={(event) =>
                                handleItemChange(index, 'quantity', event.target.value)
                              }
                              className="rounded-full border border-green-200 bg-white px-4 py-2 text-gray-800 placeholder:text-gray-400"
                            />
                          </td>
                          <td className="px-4 py-3 align-top">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={row.pricePerUnit}
                              onChange={(event) =>
                                handleItemChange(
                                  index,
                                  'pricePerUnit',
                                  event.target.value
                                )
                              }
                              className="rounded-full border border-green-200 bg-white px-4 py-2 text-gray-800 placeholder:text-gray-400"
                            />
                          </td>
                          <td className="px-4 py-3 text-right align-top text-base font-semibold text-gray-800">
                            {formatCurrency(rowSubtotal)}
                          </td>
                          <td className="px-4 py-3 text-right align-top">
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(index)}
                              className="rounded-full border border-red-100 px-3 py-2 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t border-gray-200 bg-gray-50 text-gray-700">
                      <td colSpan={5} className="px-4 py-3">
                        <button
                          type="button"
                          onClick={handleAddItem}
                          className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                        >
                          Add Item
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {isMobileView && (
              <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-2 shadow-sm sm:p-1">
                {items.map((row, index) => {
                  const rowSubtotal = computeRowSubtotal(row);
                  return (
                    <div
                      key={`item-card-${index}`}
                      className="rounded-2xl border border-gray-100 p-3 sm:p-4"
                    >
                      <div className="mb-2 flex items-center justify-between sm:mb-3">
                        <p className="text-sm font-semibold text-gray-700">
                          Item #{index + 1}
                        </p>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="space-y-3 sm:space-y-4">
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Item
                          </label>
                          <Input
                            placeholder="Enter item"
                            value={row.item}
                            onChange={(event) =>
                              handleItemChange(index, 'item', event.target.value)
                            }
                            className="rounded-2xl border border-green-200 bg-gray-50 px-4 py-2 text-gray-800 placeholder:text-gray-400"
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Days
                            </label>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              placeholder="0"
                              value={row.quantity}
                              onChange={(event) =>
                                handleItemChange(index, 'quantity', event.target.value)
                              }
                              className="rounded-2xl border border-green-200 bg-gray-50 px-4 py-2 text-gray-800 placeholder:text-gray-400"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Rate per Day
                            </label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={row.pricePerUnit}
                              onChange={(event) =>
                                handleItemChange(
                                  index,
                                  'pricePerUnit',
                                  event.target.value
                                )
                              }
                              className="rounded-2xl border border-green-200 bg-gray-50 px-4 py-2 text-gray-800 placeholder:text-gray-400"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm font-semibold text-gray-700">
                          <span>Subtotal</span>
                          <span>{formatCurrency(rowSubtotal)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                  >
                    Add Item
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card.Light>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card.Light className="p-6">
            <h3 className="text-xl font-semibold text-gray-900">Customer</h3>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-500">
                  Customer Type
                </label>
                <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerSelection('existing');
                      setSelectedCustomerId('');
                      setCustomerDetails({ name: '', mobile: '', email: '' });
                    }}
                    className={`rounded-full px-4 py-1 text-xs font-semibold transition ${
                      customerSelection === 'existing'
                        ? 'bg-green-600 text-white'
                        : 'text-gray-600'
                    }`}
                  >
                    Existing Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerSelection('new');
                      setSelectedCustomerId('');
                      // Restore booking data if available
                      if (fullBookingData) {
                        setCustomerDetails({
                          name: fullBookingData.residentName || '',
                          mobile: fullBookingData.contactNumber || '',
                          email: fullBookingData.email || '',
                        });
                      }
                    }}
                    className={`rounded-full px-4 py-1 text-xs font-semibold transition ${
                      customerSelection === 'new'
                        ? 'bg-green-600 text-white'
                        : 'text-gray-600'
                    }`}
                  >
                    New Customer
                  </button>
                </div>
              </div>

              {customerSelection === 'existing' ? (
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-500">
                    Select Customer
                  </label>
                  <select
                    value={selectedCustomerId}
                    onChange={(e) => {
                      setSelectedCustomerId(e.target.value);
                      const customer = customers.find(c => (c.customer_id || c.id) === e.target.value);
                      if (customer) {
                        setCustomerDetails({
                          name: customer.customer_name || customer.name || '',
                          mobile: customer.contact_mobile || customer.contact_phone || customer.mobile || '',
                          email: customer.contact_email || customer.email || '',
                        });
                      }
                    }}
                    className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    disabled={isCustomersLoading}
                  >
                    <option value="" disabled hidden>Select a customer</option>
                    {customers.map((customer) => (
                      <option key={customer.customer_id || customer.id} value={customer.customer_id || customer.id}>
                        {customer.customer_name || customer.name || 'Unknown Customer'}
                      </option>
                    ))}
                  </select>
                  {customersError && (
                    <p className="mt-1 text-xs text-red-600">{customersError}</p>
                  )}
                  {!isCustomersLoading && !customers.length && !customersError && (
                    <p className="mt-1 text-xs text-gray-500">
                      No customers available. Please create a customer first.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      placeholder="Enter customer name"
                      value={customerDetails.name}
                      onChange={(event) =>
                        handleCustomerChange('name', event.target.value)
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      Mobile Number
                    </label>
                    <Input
                      type="tel"
                      placeholder="Enter mobile number"
                      value={customerDetails.mobile}
                      onChange={(event) =>
                        handleCustomerChange('mobile', event.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      Email
                    </label>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={customerDetails.email}
                      onChange={(event) =>
                        handleCustomerChange('email', event.target.value)
                      }
                    />
                  </div>
                </>
              )}
            </div>
          </Card.Light>

          <Card.Light className="p-6">
            <h3 className="text-xl font-semibold text-gray-900">
              Bill Summary
            </h3>
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between text-gray-600">
                <span>Subtotal</span>
                <span className="text-lg font-semibold text-gray-900">
                  {formatCurrency(subtotal)}
                </span>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="mb-3 text-sm font-semibold text-gray-700">
                  Taxes
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      CGST (%)
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="0"
                      value={taxRates.cgst}
                      onChange={(event) =>
                        handleTaxChange('cgst', event.target.value)
                      }
                    />
                  </div>
                  <div className="text-right text-lg font-semibold text-gray-900">
                    {formatCurrency(cgstAmount)}
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      SGST (%)
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="0"
                      value={taxRates.sgst}
                      onChange={(event) =>
                        handleTaxChange('sgst', event.target.value)
                      }
                    />
                  </div>
                  <div className="text-right text-lg font-semibold text-gray-900">
                    {formatCurrency(sgstAmount)}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-gray-100 pt-4 text-gray-900">
                <span className="text-xl font-semibold">Total</span>
                <span className="text-2xl font-bold text-emerald-700">
                  {formatCurrency(grandTotal)}
                </span>
              </div>
            </div>
          </Card.Light>
        </div>

        <div className="flex flex-col gap-3">
          {submitFeedback.success && (
            <div className="rounded-2xl bg-green-50 border border-green-200 p-4">
              <p className="text-sm text-green-700">{submitFeedback.success}</p>
            </div>
          )}
          {submitFeedback.error && (
            <div className="rounded-2xl bg-red-50 border border-red-200 p-4">
              <p className="text-sm text-red-700">{submitFeedback.error}</p>
            </div>
          )}
          <div className="flex justify-end">
            <PrimaryButton
              type="submit"
              disabled={!isBillReady || isSubmitting}
              className="w-full max-w-xs"
            >
              {isSubmitting ? 'Creating Bill...' : 'Generate Bill'}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </>
  );
};

export default FacilityBookingBillCustomer;
